import cloneDeep from 'lodash/cloneDeep';
import { encryptCataloguePassword, decryptCataloguePassword } from './cataloguePasswordUtils';

/**
 * CENTRALIZED MAPPING UTILITIES
 * Tutte le funzioni di mapping tra struttura form e struttura database sono centralizzate qui
 * per evitare duplicazioni e inconsistenze
 */

/**
 * Rimuove ricorsivamente tutti i valori undefined da un oggetto per evitare errori Firestore
 * @param {Object} obj - L'oggetto da sanitizzare
 * @returns {Object} - L'oggetto sanitizzato senza valori undefined
 */

/**
 * Determina quale opzione radio è selezionata per l'altezza desktop
 * @param {number} height - L'altezza in pixel
 * @returns {string} - L'opzione selezionata ('100', '120', '150', '200', o 'custom')
 */
const determineHeightOption = (height) => {
    const predefinedOptions = [100, 120, 150, 200];
    if (predefinedOptions.includes(height)) {
        return height.toString();
    }
    return 'custom';
};

/**
 * Determina quale opzione radio è selezionata per l'altezza mobile
 * @param {number} height - L'altezza in pixel
 * @returns {string} - L'opzione selezionata ('100', '120', '150', '200', o 'custom')
 */
const determineHeightMobileOption = (height) => {
    const predefinedOptions = [100, 120, 150, 200];
    if (predefinedOptions.includes(height)) {
        return height.toString();
    }
    return 'custom';
};

/**
 * Mappa i dati dalla struttura database alla struttura form (database -> form)
 * Questa funzione converte i campi "piatti" del database nella struttura nidificata del form
 * @param {Object} data - Dati dalla struttura database
 * @returns {Object} - Dati nella struttura form
 */
export const mapDatabaseToForm = (data) => {
    const result = cloneDeep(data);

    // Initialize nested objects if they don't exist
    if (!result.seo) result.seo = {};
    if (!result.seo.metaDescriptions) result.seo.metaDescriptions = {};
    if (!result.permissions) result.permissions = {};

    // === SEO FIELDS MAPPING (database -> form) ===
    // Basic SEO fields
    if (result.titleSeo !== undefined) result.seo.title = result.titleSeo;
    if (result.list_keywords !== undefined) result.seo.keywords = result.list_keywords || [];
    if (result.featuredImage !== undefined) result.seo.featuredImage = result.featuredImage;
    if (result.metaDescription !== undefined) result.seo.metaDescription = result.metaDescription;

    // Multilingual descriptions (database -> form nested structure)
    if (result.descriptionIt !== undefined) {
        result.seo.metaDescriptions.it = result.descriptionIt;
    } else if (result.metaDescription !== undefined) {
        // Fallback: use metaDescription as Italian if descriptionIt is not available
        result.seo.metaDescriptions.it = result.metaDescription;
    }

    if (result.descriptionEn !== undefined) result.seo.metaDescriptions.en = result.descriptionEn;
    if (result.descriptionFr !== undefined) result.seo.metaDescriptions.fr = result.descriptionFr;
    if (result.descriptionEs !== undefined) result.seo.metaDescriptions.es = result.descriptionEs;
    if (result.descriptionDe !== undefined) result.seo.metaDescriptions.de = result.descriptionDe;

    // === BASIC FIELDS MAPPING (database -> form) ===
    if (result.nameCatalog !== undefined) result.catalogueName = result.nameCatalog;
    if (result.webDomain !== undefined) result.domain = result.webDomain;

    // === PERMISSIONS FIELDS MAPPING (database -> form) ===
    if (result.isLoginRequired !== undefined) result.permissions.requireLogin = result.isLoginRequired;
    if (result.isIframeOnlyAccess !== undefined) result.permissions.iframeOnlyAccess = result.isIframeOnlyAccess;
    if (result.allowedDomain !== undefined) result.permissions.allowedDomain = result.allowedDomain;
    if (result.loginPassword !== undefined) {
        // Decrypt the password to display it in the form
        try {
            result.permissions.loginPassword = decryptCataloguePassword(result.loginPassword);
        } catch (error) {
            console.error('Error decrypting password for form display:', error);
            result.permissions.loginPassword = ''; // Use empty string if decryption fails
        }
    }
    if (result.isPublic !== undefined) result.permissions.public = result.isPublic;

    // === CUSTOMIZATION FIELDS MAPPING (database -> form) ===
    if (result.catalogues_page_style) {
        const style = result.catalogues_page_style;
        result.customization = {
            // Color fields
            primaryColor: style.primaryColor,
            secondaryColor: style.secondaryColor,
            backgroundColor: style.backgroundColor || '#FFFFFF',
            actionButtonColor: style.actionButtonColor,
            bannerBackgroundColor: style.bannerBackgroundColor,
            // Border radius values
            cardCornerTopLeft: style.cardCornerTopLeft ?? 0,
            cardCornerTopRight: style.cardCornerTopRight ?? 0,
            cardCornerBottomRight: style.cardCornerBottomRight ?? 0,
            cardCornerBottomLeft: style.cardCornerBottomLeft ?? 0,
            buttonCornerTopLeft: style.buttonCornerTopLeft ?? 0,
            buttonCornerTopRight: style.buttonCornerTopRight ?? 0,
            buttonCornerBottomRight: style.buttonCornerBottomRight ?? 0,
            buttonCornerBottomLeft: style.buttonCornerBottomLeft ?? 0,
            bannerCornerTopLeft: style.bannerCornerTopLeft ?? 0,
            bannerCornerTopRight: style.bannerCornerTopRight ?? 0,
            bannerCornerBottomRight: style.bannerCornerBottomRight ?? 0,
            bannerCornerBottomLeft: style.bannerCornerBottomLeft ?? 0
        };

        // Banner fields
        if (!result.banner) result.banner = {};
        result.banner = {
            ...result.banner,
            logo: style.catalogueLogo,
            logoPosition: style.catalogueLogoPosition || 'center',
            // ✅ USA I NOMI CORRETTI DEL DATABASE PER LE ALTEZZE
            coverHeightDesktop: style.coverHeightDesktop || 100,
            coverHeightMobile: style.coverHeightMobile || 100,
            // ✅ CAMPI DELL'INTERFACCIA ORIGINALE (sincronizzati con i database)
            height: style.coverHeightDesktop || 100,
            heightMobile: style.coverHeightMobile || 100,
            // ✅ CAMPI PER L'UI DEI RADIO BUTTON - DETERMINA L'OPZIONE SELEZIONATA
            heightOption: determineHeightOption(style.coverHeightDesktop || 100),
            heightMobileOption: determineHeightMobileOption(style.coverHeightMobile || 100),
            bannerImage: style.coverImg,
            bannerImageMobile: style.coverImgMobile,
            isLogoVisible: style.isLogoVisible ?? true,
            isTitleVisible: style.isTitleVisible ?? true
        };
    }

    return result;
};

/**
 * Mappa i dati dalla struttura form alla struttura database (form -> database)
 * Questa funzione converte la struttura nidificata del form nei campi "piatti" del database
 * @param {Object} data - Dati nella struttura form
 * @returns {Object} - Dati nella struttura database
 */
export const mapFormToDatabase = (data) => {
    const result = cloneDeep(data);

    // === SEO FIELDS MAPPING (form -> database) ===
    if (result.seo) {
        // Basic SEO fields
        if (result.seo.title !== undefined) result.titleSeo = result.seo.title;
        if (result.seo.keywords !== undefined) result.list_keywords = result.seo.keywords || [];
        if (result.seo.featuredImage !== undefined) result.featuredImage = result.seo.featuredImage;
        if (result.seo.metaDescription !== undefined) result.metaDescription = result.seo.metaDescription;

        // Multilingual descriptions (form nested -> database flat)
        if (result.seo.metaDescriptions) {
            if (result.seo.metaDescriptions.it !== undefined) {
                result.descriptionIt = result.seo.metaDescriptions.it;
                // Use Italian as fallback for legacy metaDescription if not already set
                if (!result.metaDescription) {
                    result.metaDescription = result.seo.metaDescriptions.it;
                }
            }
            if (result.seo.metaDescriptions.en !== undefined) result.descriptionEn = result.seo.metaDescriptions.en;
            if (result.seo.metaDescriptions.fr !== undefined) result.descriptionFr = result.seo.metaDescriptions.fr;
            if (result.seo.metaDescriptions.es !== undefined) result.descriptionEs = result.seo.metaDescriptions.es;
            if (result.seo.metaDescriptions.de !== undefined) result.descriptionDe = result.seo.metaDescriptions.de;
        }

        // Remove the nested seo object after mapping
        delete result.seo;
    }

    // === BASIC FIELDS MAPPING (form -> database) ===
    if (result.catalogueName !== undefined && result.catalogueName !== null) result.nameCatalog = result.catalogueName;
    if (result.domain !== undefined && result.domain !== null) result.webDomain = result.domain;

    // === PERMISSIONS FIELDS MAPPING (form -> database) ===
    if (result.permissions) {
        if (result.permissions.requireLogin !== undefined) result.isLoginRequired = result.permissions.requireLogin;
        if (result.permissions.iframeOnlyAccess !== undefined) result.isIframeOnlyAccess = result.permissions.iframeOnlyAccess;
        if (result.permissions.allowedDomain !== undefined) result.allowedDomain = result.permissions.allowedDomain;
        if (result.permissions.loginPassword !== undefined) {
            // Encrypt the password before saving it to the database
            if (result.permissions.loginPassword && result.permissions.loginPassword.trim() !== '') {
                try {
                    result.loginPassword = encryptCataloguePassword(result.permissions.loginPassword);
                } catch (error) {
                    console.error('Error encrypting password for database:', error);
                    // In case of encryption error, use null to avoid saving an insecure password
                    result.loginPassword = null;
                }
            } else {
                result.loginPassword = null; // Empty or missing password
            }
        }
        if (result.permissions.public !== undefined) result.isPublic = result.permissions.public;

        // Remove the nested permissions object after mapping
        delete result.permissions;
    }

    return result;
};

/**
 * Sincronizza i campi SEO tra struttura nidificata e piatta
 * Questa funzione è un wrapper per mantenere compatibilità con il codice esistente
 * @param {Object} data - Dati da sincronizzare
 * @returns {Object} - Dati sincronizzati
 */
export const synchronizeSeoFields = (data) => {
    // Usa il mapping centralizzato per sincronizzare
    let result = mapDatabaseToForm(data);
    result = mapFormToDatabase(result);
    return result;
};

/**
 * Handles the modification of a catalogue by processing form data and saving it as a draft
 * 
 * @param {Object} options - The options for modifying the catalogue
 * @param {Object} options.formData - The form data to process
 * @param {Object} options.form - The Ant Design form instance
 * @param {string} options.originalCatalogueId - ID of the original published catalogue
 * @param {string} [options.existingDraftId] - ID of existing draft if updating
 * @param {Function} options.messageApi - Ant Design message API for notifications
 * @param {Function} [options.onSuccess] - Callback function called on successful save
 * @returns {Promise<string>} - Returns the drafted catalogue ID on success
 */
export const handleModifyCatalogue = async ({
    formData,
    form,
    originalCatalogueId,
    existingDraftId = null,
    messageApi,
    onSuccess
}) => {
    try {
        // Validate form fields if form instance is provided
        if (form) {
            await form.validateFields();
        }

        // Use the SAME system that works for drafted catalogues
        if (existingDraftId) {
            // If updating an existing draft, use the working drafted catalogue system
            const { updateDraftedCatalogue } = await import('../api/catalogueDraftService');
            const draftedCatalogueId = await updateDraftedCatalogue(existingDraftId, formData);

            if (draftedCatalogueId) {
                messageApi.success('Changes saved successfully');
                if (onSuccess) {
                    onSuccess(formData, draftedCatalogueId);
                }
                return draftedCatalogueId;
            } else {
                throw new Error('Failed to save changes, no ID returned.');
            }
        } else {
            // For new drafts from published catalogues, use the working system
            const { savePublishedCatalogAsDraft } = await import('../api/draftedCatalogueService');
            const draftedCatalogueId = await savePublishedCatalogAsDraft(originalCatalogueId, formData);

            if (draftedCatalogueId) {
                messageApi.success('Changes saved successfully');
                if (onSuccess) {
                    onSuccess(formData, draftedCatalogueId);
                }
                return draftedCatalogueId;
            } else {
                throw new Error('Failed to save changes, no ID returned.');
            }
        }
    } catch (error) {
        console.error('Error saving changes:', error);
        const errorMessage = error.errorFields ? 'Please fix the validation errors.' : (error.message || 'Failed to save changes');
        messageApi.error(errorMessage);
        throw error;
    }
};

/**
 * Handles the modification of a catalogue from form instance data (for modal usage)
 * This function merges form data with latest form values before processing
 * 
 * @param {Object} options - The options for modifying the catalogue
 * @param {Object} options.draftFormData - The base draft form data
 * @param {Object} options.formInstance - The Ant Design form instance
 * @param {string} options.originalCatalogueId - ID of the original published catalogue
 * @param {string} [options.existingDraftId] - ID of existing draft if updating
 * @param {Function} options.messageApi - Ant Design message API for notifications
 * @param {Function} [options.onSuccess] - Callback function called on successful save
 * @returns {Promise<string>} - Returns the drafted catalogue ID on success
 */
export const handleModifyCatalogueFromForm = async ({
    draftFormData,
    formInstance,
    originalCatalogueId,
    existingDraftId = null,
    messageApi,
    onSuccess
}) => {
    if (!originalCatalogueId) {
        throw new Error('Original catalogue ID is missing');
    }

    if (!formInstance) {
        throw new Error('Form instance is not available to save changes.');
    }

    // Use current form values directly - no need for complex merging
    // since the form instance already contains the most up-to-date data
    const currentFormData = formInstance.getFieldsValue(true);

    // Use the main handleModifyCatalogue function
    return await handleModifyCatalogue({
        formData: currentFormData,
        form: formInstance,
        originalCatalogueId,
        existingDraftId: existingDraftId,
        messageApi,
        onSuccess
    });
}; 